import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, BedDouble, X } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import IcalSyncSection from "@/components/IcalSyncSection";
import { roomHeadlinePrice } from "@/lib/pricing";
import { MultiPhotoUploader, absUrl } from "@/components/PhotoUploader";

export default function RoomsTab({ propertyId, refreshTop }) {
  const { t, lang } = useLang();
  const [rooms, setRooms] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const emptyForm = { name: "", description_it: "", description_en: "", price_per_night: 100, capacity: 2, photo: "", category: "standard", photos: [], occupancy_prices: [], no_show_penalty: "", access_codes: "" };
  const [form, setForm] = useState(emptyForm);

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setDlg(true); };
  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      name: r.name || "", description_it: r.description_it || "", description_en: r.description_en || "",
      price_per_night: r.price_per_night || 0, capacity: r.capacity || 2,
      photo: r.photo || "", category: r.category || "standard",
      photos: r.photos || [], occupancy_prices: r.occupancy_prices || [],
      no_show_penalty: r.no_show_penalty || "",
      access_codes: r.access_codes || "",
    });
    setDlg(true);
  };

  const CATEGORIES = [
    { v: "standard",  label_it: "Standard", label_en: "Standard", color: "bg-muted text-foreground" },
    { v: "comfort",   label_it: "Comfort",  label_en: "Comfort",  color: "bg-sage/15 text-sage" },
    { v: "deluxe",    label_it: "Deluxe",   label_en: "Deluxe",   color: "bg-primary/15 text-primary" },
    { v: "suite",     label_it: "Suite",    label_en: "Suite",    color: "bg-primary text-primary-foreground" },
  ];
  const catMeta = (v) => CATEGORIES.find(c => c.v === v) || CATEGORIES[0];

  const load = () => api.get("/rooms/me").then((r) => setRooms(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const payload = { ...form, photo: form.photo || form.photos[0] || "" };
      if (editingId) {
        await api.put(`/rooms/${editingId}`, payload);
      } else {
        await api.post("/rooms", payload);
      }
      toast.success(t("dash.saved"));
      setDlg(false);
      setEditingId(null);
      setForm(emptyForm);
      load();
    } catch { toast.error(t("dash.toast_error")); }
  };
  const del = async (id) => { await api.delete(`/rooms/${id}`); load(); };

  // Group rooms by category for display
  const groupsOrder = ["suite", "deluxe", "comfort", "standard"];
  const grouped = groupsOrder
    .map(cat => ({ cat, items: rooms.filter(r => (r.category || "standard") === cat) }))
    .filter(g => g.items.length > 0);

  // Summary of rooms by capacity
  const CAPACITY_BUCKETS = [
    { cap: 1, key: "single", label_it: "Singola",   label_en: "Single",   plural_it: "Singole",   plural_en: "Singles" },
    { cap: 2, key: "double", label_it: "Doppia",    label_en: "Double",   plural_it: "Doppie",    plural_en: "Doubles" },
    { cap: 3, key: "triple", label_it: "Tripla",    label_en: "Triple",   plural_it: "Triple",    plural_en: "Triples" },
    { cap: 4, key: "quad",   label_it: "Quadrupla", label_en: "Quad",     plural_it: "Quadruple", plural_en: "Quads" },
  ];
  const capacityCounts = CAPACITY_BUCKETS.map(b => ({ ...b, count: rooms.filter(r => (r.capacity || 0) === b.cap).length }));
  const multipleCount = rooms.filter(r => (r.capacity || 0) >= 5).length;
  if (multipleCount > 0) {
    capacityCounts.push({ cap: "5+", key: "multiple", label_it: "Multipla", label_en: "Multi-bed", plural_it: "Multiple", plural_en: "Multi-bed", count: multipleCount });
  }
  const totalRoomsCount = rooms.length;

  return (
    <div>
      {totalRoomsCount > 0 && (
        <div className="mb-4 border border-border bg-sand/30 rounded-lg p-4" data-testid="rooms-summary-by-capacity">
          <div className="flex items-baseline justify-between mb-3">
            <div className="uppercase tracking-widest text-[10px] text-sage font-semibold">
              {lang === "it" ? "Camere per tipologia" : "Rooms by type"}
            </div>
            <div className="text-xs text-muted-foreground">
              {lang === "it" ? "Totale" : "Total"}: <span className="font-semibold text-foreground">{totalRoomsCount}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {capacityCounts.filter(b => b.count > 0).map(b => (
              <div
                key={b.key}
                data-testid={`rooms-count-${b.key}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-card text-sm lift"
                title={lang === "it"
                  ? `${b.count} camere ${b.plural_it.toLowerCase()} (${b.cap} ${b.cap === 1 ? "posto" : "posti"})`
                  : `${b.count} ${b.plural_en.toLowerCase()} rooms (${b.cap} ${b.cap === 1 ? "bed" : "beds"})`}
              >
                <BedDouble className="w-3.5 h-3.5 text-primary"/>
                <span className="font-semibold text-foreground">{b.count}</span>
                <span className="text-muted-foreground">{lang === "it" ? (b.count === 1 ? b.label_it : b.plural_it) : (b.count === 1 ? b.label_en : b.plural_en)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex justify-end mb-4">
        <Button onClick={openCreate} className="bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="add-room-btn"><Plus className="w-4 h-4 mr-1"/>{t("dash.add_room")}</Button>
        <Dialog open={dlg} onOpenChange={(o) => { setDlg(o); if (!o) { setEditingId(null); setForm(emptyForm); } }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editingId ? (lang === "it" ? "Modifica camera" : "Edit room") : t("dash.add_room")}</DialogTitle></DialogHeader>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto" data-testid="add-room-form">
              <div><Label>{t("dash.room_name")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="room-name"/></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>{t("dash.price_night")}</Label><Input type="number" step="0.01" value={form.price_per_night} onChange={(e) => setForm({ ...form, price_per_night: parseFloat(e.target.value) })} data-testid="room-price"/></div>
                <div><Label>{t("dash.capacity")}</Label><Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) })}/></div>
                <div>
                  <Label>{lang === "it" ? "Categoria" : "Category"}</Label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-transparent text-sm"
                    data-testid="room-category-select"
                  >
                    {CATEGORIES.map(c => <option key={c.v} value={c.v}>{lang === "it" ? c.label_it : c.label_en}</option>)}
                  </select>
                </div>
              </div>
              <div><Label>{t("dash.desc_it")}</Label><Textarea rows={2} value={form.description_it} onChange={(e) => setForm({ ...form, description_it: e.target.value })}/></div>
              <div><Label>{t("dash.desc_en")}</Label><Textarea rows={2} value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })}/></div>

              <div>
                <Label>{lang === "it" ? "Politica di cancellazione" : "Cancellation policy"}</Label>
                <select
                  value={form.no_show_penalty || ""}
                  onChange={(e) => setForm({ ...form, no_show_penalty: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-input bg-transparent text-sm"
                  data-testid="room-cancellation-policy"
                >
                  <option value="">
                    {lang === "it" ? "Usa policy struttura (default)" : "Use property policy (default)"}
                  </option>
                  <option value="free_1d">{lang === "it" ? "Gratuita fino a 1 giorno prima" : "Free up to 1 day before"}</option>
                  <option value="free_3d">{lang === "it" ? "Gratuita fino a 3 giorni prima" : "Free up to 3 days before"}</option>
                  <option value="free_5d">{lang === "it" ? "Gratuita fino a 5 giorni prima" : "Free up to 5 days before"}</option>
                  <option value="first_night">{lang === "it" ? "Penale: prima notte" : "Penalty: first night"}</option>
                  <option value="30_percent">{lang === "it" ? "Penale: 30% del totale" : "Penalty: 30% of total"}</option>
                  <option value="full">{lang === "it" ? "Penale: 100% (nessun rimborso)" : "Penalty: 100% (no refund)"}</option>
                </select>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {lang === "it"
                    ? "Sovrascrive la policy della struttura per questa camera (utile per Deluxe/Vista Mare)."
                    : "Overrides the property policy for this room (useful for Deluxe/Sea View)."}
                </p>
              </div>
              <div>
                <Label>{lang === "it" ? "Galleria foto (multipla)" : "Photo gallery (multiple)"}</Label>
                <MultiPhotoUploader values={form.photos || []} onChange={(arr) => setForm({ ...form, photos: arr, photo: form.photo || arr[0] || "" })} testid="room-photos-uploader"/>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {lang === "it" ? "La prima foto sarà la copertina della camera. Carica più immagini per una galleria completa." : "First photo becomes the room cover. Upload more for a full gallery."}
                </p>
              </div>

              {/* Occupancy pricing tiers */}
              <div className="border border-border rounded-md p-3" data-testid="occupancy-prices-section">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-[11px] uppercase tracking-widest">{lang === "it" ? "Tariffe per numero di occupanti" : "Per-occupancy rates"}</Label>
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={() => {
                      const arr = [...(form.occupancy_prices || [])];
                      const nextOcc = Math.min(form.capacity || 2, (arr[arr.length - 1]?.occupants || 0) + 1 || 1);
                      arr.push({ occupants: nextOcc, price: form.price_per_night });
                      setForm({ ...form, occupancy_prices: arr });
                    }}
                    data-testid="add-occupancy-tier"
                  >
                    <Plus className="w-3 h-3 mr-1"/>{lang === "it" ? "Aggiungi fascia" : "Add tier"}
                  </Button>
                </div>
                {(form.occupancy_prices || []).length === 0 ? (
                  <p className="text-[11px] text-muted-foreground">
                    {lang === "it"
                      ? "Facoltativo. Se lasciato vuoto viene usato il prezzo base sopra. Esempio: 1 ospite €90, 2 ospiti €120, 3 ospiti €150."
                      : "Optional. If empty, the base price above is used. Example: 1 guest €90, 2 guests €120, 3 guests €150."}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {form.occupancy_prices.map((op, i) => (
                      <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end" data-testid={`occupancy-row-${i}`}>
                        <div>
                          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">{lang === "it" ? "Occupanti" : "Occupants"}</label>
                          <Input
                            type="number" min={1} max={form.capacity || 20}
                            value={op.occupants}
                            onChange={(e) => {
                              const arr = [...form.occupancy_prices];
                              arr[i] = { ...arr[i], occupants: parseInt(e.target.value) || 1 };
                              setForm({ ...form, occupancy_prices: arr });
                            }}
                            className="h-9" data-testid={`occupancy-count-${i}`}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">{lang === "it" ? "€ / notte" : "€ / night"}</label>
                          <Input
                            type="number" step="0.01" min={0}
                            value={op.price}
                            onChange={(e) => {
                              const arr = [...form.occupancy_prices];
                              arr[i] = { ...arr[i], price: parseFloat(e.target.value) || 0 };
                              setForm({ ...form, occupancy_prices: arr });
                            }}
                            className="h-9" data-testid={`occupancy-price-${i}`}
                          />
                        </div>
                        <Button
                          type="button" size="sm" variant="ghost"
                          onClick={() => setForm({ ...form, occupancy_prices: form.occupancy_prices.filter((_, j) => j !== i) })}
                          className="h-9 text-destructive hover:text-destructive"
                          data-testid={`occupancy-remove-${i}`}
                        >
                          <X className="w-4 h-4"/>
                        </Button>
                      </div>
                    ))}
                    <p className="text-[10px] text-muted-foreground">
                      {lang === "it"
                        ? "Verrà applicata la fascia con il numero di occupanti più alto ≤ ospiti richiesti."
                        : "The tier with the highest occupants ≤ requested guests will be applied."}
                    </p>
                  </div>
                )}
              </div>
              <div>
                <Label>{lang === "it" ? "Codici d'accesso" : "Access codes"}</Label>
                <Textarea
                  rows={3}
                  value={form.access_codes || ""}
                  onChange={(e) => setForm({ ...form, access_codes: e.target.value })}
                  placeholder={lang === "it"
                    ? "Wi-Fi: MyHouse · pwd 1234\nPortone: 5678#\nCassaforte: 0000"
                    : "Wi-Fi: MyHouse · pwd 1234\nFront door: 5678#\nSafe: 0000"}
                  className="font-mono text-xs"
                  data-testid="room-access-codes"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  {lang === "it"
                    ? "Verranno inviati all'ospite via WhatsApp appena completa il Self Check-in."
                    : "Sent to the guest via WhatsApp as soon as they complete Self Check-in."}
                </p>
              </div>
              <Button onClick={create} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="room-save-btn">{editingId ? (lang === "it" ? "Salva modifiche" : "Save changes") : t("dash.save")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {rooms.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-12 border border-dashed border-border rounded-lg">{t("dash.no_rooms")}</div>
      ) : (
        <div className="space-y-8" data-testid="rooms-list">
          {grouped.map(({ cat, items }) => {
            const m = catMeta(cat);
            return (
              <div key={cat} data-testid={`room-group-${cat}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className={`inline-block text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 ${m.color}`}>
                    {lang === "it" ? m.label_it : m.label_en}
                  </span>
                  <span className="text-xs text-muted-foreground">{items.length} {lang === "it" ? (items.length === 1 ? "camera" : "camere") : (items.length === 1 ? "room" : "rooms")}</span>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {items.map((r) => (
                    <div key={r.id} className="border border-border rounded-lg p-4 bg-card" data-testid={`dash-room-${r.id}`}>
                      <div className="flex gap-4">
                        <div className="w-24 h-24 rounded-md bg-muted overflow-hidden shrink-0 relative">
                          {(r.photo || r.photos?.[0]) && <img src={absUrl(r.photo || r.photos[0])} className="w-full h-full object-cover" alt={r.name}/>}
                          {r.photos?.length > 1 && (
                            <div className="absolute bottom-1 right-1 text-[10px] bg-black/60 text-white rounded-full px-1.5 py-0.5">+{r.photos.length - 1}</div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h3 className="font-display text-xl leading-tight">{r.name}</h3>
                              <div className="text-xs text-muted-foreground">Cap: {r.capacity}</div>
                            </div>
                            <div className="flex gap-1">
                              <button onClick={() => openEdit(r)} className="text-muted-foreground hover:text-primary" title={lang === "it" ? "Modifica" : "Edit"} data-testid={`edit-room-${r.id}`}><Pencil className="w-4 h-4"/></button>
                              <button onClick={() => del(r.id)} className="text-muted-foreground hover:text-destructive" data-testid={`del-room-${r.id}`}><Trash2 className="w-4 h-4"/></button>
                            </div>
                          </div>
                          {(() => {
                            const hp = roomHeadlinePrice(r);
                            return (
                              <div className="mt-2 font-display text-2xl text-primary" data-testid={`room-display-price-${r.id}`}>
                                {hp.fromRange && <span className="text-[10px] text-muted-foreground align-top mr-0.5">{lang === "it" ? "da" : "from"} </span>}
                                € {hp.price.toFixed(0)}
                                <span className="text-xs text-muted-foreground ml-1">/{t("dash.night")}</span>
                                {hp.hasTiers && (
                                  <span className="ml-1.5 text-[9px] uppercase tracking-widest bg-sage/15 text-sage px-1.5 py-0.5 rounded-full align-middle" title={lang === "it" ? "Tariffa per occupanti" : "Occupancy pricing"}>
                                    {lang === "it" ? "occ." : "occ."}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      <IcalSyncSection roomId={r.id}/>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
